/**
 * A room's Page and offer in Season: the sign-up page's link and numbers, what it asks for, the confirmation email and
 * the offer. The iPad on its side also shows the page itself in a phone frame, in its right pane.
 */
import type { ReactNode } from "react";
import { useNavigate } from "react-router";
import type { WebinarDetail } from "../../../shared/types";
import { boardPath } from "../../board/paths";
import { GroupedList } from "../../components/season/GroupedList";
import type { SeasonLayout } from "../../components/season/layout";
import { SeasonCard } from "../../components/season/Card";
import { GroupHead } from "../../components/season/PageHead";
import { Button } from "../../components/ui/Button";
import { Icon } from "../../components/ui/Icon";
import { Pill } from "../../components/ui/Pill";
import { Toggle } from "../../components/ui/Toggle";
import { OfferSheet } from "./PageTab";
import { LinkRow } from "./seasonRoomParts";
import { linkWords, usePageDoc, usePageTab } from "./usePageTab";

const n = (v: number) => v.toLocaleString("en-US");

function Rows({ rows }: { rows: Array<[string, ReactNode]> }) {
  return (
    <ul className="k-gl k-kv rm-flat">
      {rows.map(([k, v]) => (
        <li key={k} className="k-lr">
          <div className="k-lr-b">
            <span className="k-k">{k}</span>
            <span className="k-v">{v}</span>
          </div>
        </li>
      ))}
    </ul>
  );
}

export function PageSection({ detail, layout, onChanged }: { detail: WebinarDetail; layout: SeasonLayout; onChanged: () => void }) {
  const navigate = useNavigate();
  const { visits, registered, rate, phone, fixed, extra, confirm, busy, asking, flipPhone, flipConfirm, offer, setOffer, editing, setEditing } = usePageTab(detail, onChanged);
  const published = detail.status === "published";
  const signup = (
    <SeasonCard>
      <h2 className="k-card-h">The sign-up page</h2>
      <LinkRow url={detail.register_url} />
      <div className="rm-btns2">
        {published ? (
          <Button icon="eye" onClick={() => window.open(detail.register_url, "_blank", "noopener")}>
            Preview
          </Button>
        ) : null}
        {layout === "side" && detail.board ? (
          <Button icon="pencil" onClick={() => navigate(boardPath(detail.id))}>
            Edit the page
          </Button>
        ) : null}
      </div>
      <Rows
        rows={[
          ["Visits", n(visits)],
          ["Registered", n(registered)],
          ["Of visitors who registered", `${rate}%`],
        ]}
      />
    </SeasonCard>
  );
  const registration = (
    <>
      <GroupHead title="Registration" />
      <GroupedList noLead>
        {fixed.map((f) => (
          <li key={f.key} className="k-lr">
            <div className="k-set">
              <span className="k-set-t">
                <b>{f.label}</b>
              </span>
              <Pill tone="line" icon="lock">
                Always asked
              </Pill>
            </div>
          </li>
        ))}
        <li className="k-lr">
          <div className="k-set">
            <span className="k-set-t">
              <b>Phone</b>
            </span>
            <Toggle on={phone} onChange={flipPhone} label="Phone" disabled={asking} />
          </div>
        </li>
        {extra.map((f) => (
          <li key={f.key} className="k-lr">
            <div className="k-set">
              <span className="k-set-t">
                <b>{f.label}</b>
              </span>
              <Pill tone="line">{f.required ? "Required" : "Asked"}</Pill>
            </div>
          </li>
        ))}
        <li className="k-lr">
          <div className="k-set">
            <span className="k-set-t">
              <b>Confirmation email</b>
            </span>
            <Toggle on={confirm} onChange={flipConfirm} label="Confirmation email" disabled={busy} />
          </div>
        </li>
      </GroupedList>
    </>
  );
  const offerCard = (
    <SeasonCard className="rm-offer">
      <h2 className="k-card-h">
        The offer
        {offer?.label ? (
          <Button icon="pencil" onClick={() => setEditing(true)}>
            Edit
          </Button>
        ) : null}
      </h2>
      {offer?.label ? (
        <Rows
          rows={[
            ["Button", <Pill key="b" tone="wine">{offer.label}</Pill>],
            ...(offer.url ? ([["Where it goes", linkWords(offer.url)]] as Array<[string, ReactNode]>) : []),
            ...(offer.appears_at !== undefined ? ([["Appears at", `${Math.round(offer.appears_at / 60)} min, stays to the end`]] as Array<[string, ReactNode]>) : []),
            [
              "Clicked",
              <>
                {n(detail.chain.clicked)} {detail.chain.clicked === 1 ? "person" : "people"}
                <small>
                  {n(detail.chain.customers)} became {detail.chain.customers === 1 ? "a customer" : "customers"}
                </small>
              </>,
            ],
          ]}
        />
      ) : (
        <p className="k-rm">No offer on this room yet.</p>
      )}
    </SeasonCard>
  );
  return (
    <>
      {layout === "upright" ? (
        <div className="k-cols rm-cols">
          <div>
            {signup}
            {registration}
          </div>
          <div>{offerCard}</div>
        </div>
      ) : (
        <>
          {signup}
          {registration}
          <div className="rm-gap" />
          {offerCard}
        </>
      )}
      {editing && offer ? (
        <OfferSheet
          roomId={detail.id}
          hasBoard={detail.board}
          offer={offer}
          onClose={() => setEditing(false)}
          onSaved={(saved) => {
            setOffer(saved);
            onChanged();
          }}
        />
      ) : null}
    </>
  );
}

/** The width a phone draws the page at, and the width the frame's screen shows it at. */
const GUEST_W = 390;
const SCREEN_W = 212;

/** The sign-up page itself in a phone frame: the iPad's right pane beside Page and offer. */
export function PagePhone({ detail }: { detail: WebinarDetail }) {
  const doc = usePageDoc(detail);
  return (
    <SeasonCard className="rm-prevc">
      <h2 className="k-card-h">The sign-up page</h2>
      <div className="rm-phone" role="img" aria-label="The sign-up page as your people see it">
        <div className="rm-pscreen">
          {doc ? (
            <iframe
              title="The sign-up page"
              srcDoc={doc}
              sandbox=""
              scrolling="no"
              tabIndex={-1}
              aria-hidden="true"
              style={{ width: GUEST_W, transform: `scale(${SCREEN_W / GUEST_W})` }}
            />
          ) : (
            <Icon name="globe" />
          )}
        </div>
      </div>
    </SeasonCard>
  );
}
